import React, {
  useEffect,
  useState,
} from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import {
  ExpandMore,
  HelpOutline,
  LocalHospital,
  Toc,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { Tooltip } from '@mui/material';

import { routePaths } from '@/core/constants/routes';
import {
  isMultipleLineOfBusinessLoading, selectSelectedLineOfBusiness,
} from '@/core/store/reducers/staticContent';

import { Select } from '@/features/MultiLine';

import {
  AccordionMenuItem,
  AccordionMenuSubItem,
  Container,
  IconContainer,
  Link,
  MenuList,
  NonAccordionItem,
  StyledAccordion,
  StyledAccordionDetails,
  StyledAccordionSummary,
  StyledDivider,
  StyledSkeleton,
  CollapsedIconContainer,
} from './Sidebar.styles';

const claimsSubItems = [
  {
    icon: null,
    label: 'CT Overview',
    path: routePaths.claimsTriage.overview,
  },
  {
    icon: null,
    label: 'CT Claims',
    path: routePaths.claimsTriage.claims,
  },
  {
    icon: null,
    label: 'CT Reports',
    path: routePaths.claimsTriage.reports,
  },
];

const medBenchSubItems = [
  {
    icon: null,
    label: 'MB Overview',
    path: routePaths.medBench.overview,
  },
  {
    icon: null,
    label: 'MB Claims',
    path: routePaths.medBench.claims,
  },
  {
    icon: null,
    label: 'Providers',
    path: routePaths.medBench.providers,
  },
  {
    icon: null,
    label: 'Treatment Benchmarks',
    path: routePaths.medBench.treatment,
  },
  {
    icon: null,
    label: 'MB Reports',
    path: routePaths.medBench.reports,
  },
];

const menuItems = [
  {
    divider: true,
    id: 'top-divider',
  },
  {
    icon: <Toc
      fontSize="inherit"
      htmlColor="inherit"
    />,
    label: 'Claims Triage',
    path: null,
    submenuName: 'claimsTriage',
    subItems: claimsSubItems,
  },
  {
    icon: <LocalHospital
      fontSize="inherit"
      htmlColor="inherit"
    />,
    label: 'Medical Benchmarking',
    path: null,
    submenuName: 'medBench',
    subItems: medBenchSubItems,
  },
  {
    divider: true,
    id: 'bottom-divider',
  },
  {
    icon: <HelpOutline
      fontSize="inherit"
      htmlColor="inherit"
    />,
    label: 'Help',
    path: routePaths.help,
  },
];

interface SidebarProps {
  isCollapsed?: boolean;
  setIsCollapsed?: (collapsed: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed = false, setIsCollapsed }) => {
  const selectedLineOfbusiness = useSelector(selectSelectedLineOfBusiness);
  const isLineOfBusinessLoading = useSelector(isMultipleLineOfBusinessLoading);
  const {
    asPath, route,
  } = useRouter();
  const [
    expandedSubmenus,
    setExpandedSubmenus,
  ] = useState<Array<string>>([]);

  const addExpandedSubmenu = (submenu = '') => setExpandedSubmenus(prevExpandedSubmenus => [
    ...prevExpandedSubmenus,
    submenu,
  ]);

  const removeExpandedSubmenu = (submenu = '') => setExpandedSubmenus(prevExpandedSubmenus => prevExpandedSubmenus.filter(
    expandedSubmenu => expandedSubmenu !== submenu
  ));

  const triggerExpandedSubmenu = (submenu = '') => {
    if (expandedSubmenus.includes(submenu)) {
      removeExpandedSubmenu(submenu);
    } else {
      addExpandedSubmenu(submenu);
    }
  };

  useEffect(() => {
    menuItems
      .filter(item => item.submenuName && item.subItems)
      .forEach(item => {
        if (item?.subItems?.some(subItem => asPath.includes(subItem?.path))) {
          addExpandedSubmenu(item.submenuName);
        } else {
          removeExpandedSubmenu(item.submenuName);
        }
      });
  }, [asPath]);

  // Close all expanded submenus when collapsing sidebar
  useEffect(() => {
    if (isCollapsed) {
      setExpandedSubmenus([]);
    }
  }, [isCollapsed]);

  const renderCollapsedMenuItem = (icon: React.ReactNode, label: string, isActive: boolean, onClick?: () => void) => (
    <Tooltip title={label} placement="right">
      <CollapsedIconContainer isActive={isActive} onClick={onClick}>
        {icon}
      </CollapsedIconContainer>
    </Tooltip>
  );

  const renderExpandedAccordion = (item: any) => (
    <>
      {!isLineOfBusinessLoading && selectedLineOfbusiness?.accessList?.some(
        accessList => accessList.accessName === item.label
      ) && (
        <StyledAccordion
          disableGutters
          elevation={0}
          square
          expanded={!!item.submenuName && expandedSubmenus.includes(item.submenuName)}
          onChange={() => triggerExpandedSubmenu(item.submenuName)}
        >
          <StyledAccordionSummary
            expandIcon={<ExpandMore fontSize="small" color="inherit" />}
          >
            <AccordionMenuItem>
              <IconContainer>{item.icon}</IconContainer>
              {item.label}
            </AccordionMenuItem>
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            {item.subItems?.map((subItem: any) => (
              <NextLink key={subItem.path} href={subItem.path} passHref>
                <Link>
                  <AccordionMenuSubItem
                    isActive={
                      subItem.path.split('/').length > 2 ?
                        asPath.includes(subItem.path, 0) :
                        route === subItem.path
                    }
                  >
                    {subItem.label}
                  </AccordionMenuSubItem>
                </Link>
              </NextLink>
            ))}
          </StyledAccordionDetails>
        </StyledAccordion>
      )}

      {isLineOfBusinessLoading && (
        <StyledAccordionSummary>
          <AccordionMenuItem>
            <IconContainer>
              <StyledSkeleton />
            </IconContainer>
            <StyledSkeleton variant="text" />
          </AccordionMenuItem>
        </StyledAccordionSummary>
      )}
    </>
  );

  return (
    <Container $isCollapsed={isCollapsed}>
      
      {!isCollapsed && <Select />}
      
      <MenuList>
        {menuItems.map((item, index) => {
          if (item.divider) {
            return !isCollapsed
              ? <StyledDivider key={item.id || `divider-${index}`} $marginTop={2} $marginBottom={2} />
              : null;
          }

          // Handle accordion items with submenus
          if (item.subItems) {
            if (isCollapsed) {
              const hasActiveSubItem = item.subItems.some((subItem: any) => (
                subItem.path.split('/').length > 2
                  ? asPath.includes(subItem.path, 0)
                  : route === subItem.path
              ));
              
              return selectedLineOfbusiness?.accessList?.some(
                (accessList: any) => accessList.accessName === item.label
              )
                ? renderCollapsedMenuItem(
                  item.icon,
                  item.label,
                  hasActiveSubItem,
                  () => {
                    setIsCollapsed?.(false);
                    triggerExpandedSubmenu(item.submenuName);
                  }
                )
                : null;
            }
            return renderExpandedAccordion(item);
          }

          // Handle regular menu items
          if (item.path) {
            const isActive = item.path.split('/').length > 2
              ? asPath.includes(item.path, 0)
              : route === item.path;

            if (isCollapsed) {
              return (
                <NextLink key={item.path} href={item.path} passHref>
                  <Link>
                    {renderCollapsedMenuItem(item.icon, item.label, isActive)}
                  </Link>
                </NextLink>
              );
            }

            return (
              <NextLink key={item.path} href={item.path} passHref>
                <Link>
                  <NonAccordionItem isActive={isActive}>
                    <AccordionMenuItem>
                      <IconContainer>{item.icon}</IconContainer>
                      {item.label}
                    </AccordionMenuItem>
                  </NonAccordionItem>
                </Link>
              </NextLink>
            );
          }

          return null;
        })}
      </MenuList>
    </Container>
  );
};
